/**
 * BLOCS COMMUNS — un groupe de questions écrit une fois, inséré dans plusieurs
 * questionnaires de qualification.
 *
 * Un bloc est un questionnaire marqué `estBloc` ; un questionnaire l'insère par
 * une question de type `bloc` (`blocQuestionnaireId`). Partout où l'on POSE les
 * questions (tunnel, questionnaire du client, PDF, demande de devis), chaque
 * insertion est remplacée par les questions du bloc : c'est `deplierBlocs`.
 * Seul l'éditeur voit l'insertion elle-même, comme une ligne déplaçable.
 *
 * Module partagé front/back : aucune dépendance à Vue ni à Prisma.
 */

/** Le type de la question qui insère un bloc. */
export const TYPE_QUESTION_BLOC = 'bloc'

/** Le code du bloc commun « Type d'établissement », repris par la fiche demande. */
export const CODE_BLOC_TYPE_ETABLISSEMENT = 'bloc-type-etablissement'

export function estInsertionDeBloc(question) {
  return question?.type === TYPE_QUESTION_BLOC
}

/** Les questions du bloc qu'une insertion désigne, telles que le dépôt les inclut. */
export function questionsDuBloc(insertion) {
  return insertion?.blocQuestionnaire?.questions || []
}

const parRang = (a, b) => (a.order || 0) - (b.order || 0) || (a.id || 0) - (b.id || 0)

/**
 * Les questions d'un questionnaire, insertions de blocs dépliées.
 *
 * Chaque insertion cède sa place aux questions de son bloc, dans leur ordre, et
 * les rangs sont renumérotés de 0 à n : les écrans qui retrient par `order` ne
 * remélangent pas le bloc avec le reste. Une question de bloc porte la partie
 * de son insertion et l'identifiant du bloc (`blocId`).
 *
 * LES CONSEILS NE SONT PAS COMMUNS : ceux que l'insertion porte pour une
 * question du bloc (`conseilsBloc`) remplacent ceux du bloc, dans ce seul
 * questionnaire.
 *
 * @param {object[]} questions les questions du questionnaire
 * @param {(insertion: object) => object[]|undefined} [lireBloc] les questions du
 *   bloc qu'une insertion désigne — par défaut `questionsDuBloc`
 * @returns {object[]}
 */
export function deplierBlocs(questions, lireBloc = questionsDuBloc) {
  const deplie = []
  for (const question of [...(questions || [])].sort(parRang)) {
    if (!estInsertionDeBloc(question)) {
      deplie.push(question)
      continue
    }
    for (const duBloc of [...(lireBloc(question) || [])].sort(parRang)) {
      if (estInsertionDeBloc(duBloc)) continue // un bloc n'en contient pas d'autre
      deplie.push({
        ...duBloc,
        conseils: conseilsPourInsertion(question, duBloc),
        partie: question.partie || duBloc.partie,
        blocId: question.blocQuestionnaireId,
      })
    }
  }
  return deplie.map((question, index) => ({ ...question, order: index }))
}

/**
 * Les conseils d'une question du bloc DANS le questionnaire de cette insertion :
 * les siens propres s'il en a, ceux du bloc sinon.
 */
export function conseilsPourInsertion(insertion, duBloc) {
  const propres = insertion?.conseilsBloc
  if (propres && typeof propres === 'object' && Object.prototype.hasOwnProperty.call(propres, String(duBloc.id))) {
    return propres[String(duBloc.id)] ?? null
  }
  return duBloc.conseils ?? null
}

/** Un questionnaire dont les insertions de blocs sont dépliées. */
export function questionnaireDeplie(questionnaire, lireBloc) {
  if (!questionnaire) return questionnaire
  return { ...questionnaire, questions: deplierBlocs(questionnaire.questions, lireBloc) }
}
